/**
 * Constancia de un hábito diario a partir del conjunto de días cumplidos (claves `yyyy-MM-dd`).
 * Traducción directa de HabitStats.swift: los 7 días son la métrica principal; las rachas cuentan, pero nunca castigan.
 */
import { addDays, dateKey } from './dates.js';

export interface HabitStats {
  last7: number;
  last30: number;
  currentStreak: number;
  bestStreak: number;
  /** "Nunca falles dos veces": fallos seguidos de vuelta al día siguiente… */
  recovered: number;
  /** …de los fallos ya valorados (un hueco abierto de un solo día aún no se valora: hoy todavía puedes volver). */
  recoveryOpportunities: number;
}

export const EMPTY_STATS: HabitStats = { last7: 0, last30: 0, currentStreak: 0, bestStreak: 0, recovered: 0, recoveryOpportunities: 0 };

export const recoveryPercent = (s: HabitStats): number | null =>
  s.recoveryOpportunities > 0 ? Math.round((s.recovered / s.recoveryOpportunities) * 100) : null;

export function habitStats(done: ReadonlySet<string>, today: string = dateKey()): HabitStats {
  if (done.size === 0) return EMPTY_STATS;
  const has = (k: string) => done.has(k);

  const countBack = (days: number) => Array.from({ length: days }, (_, i) => addDays(today, -i)).filter(has).length;

  // Racha actual: días seguidos que terminan hoy (o ayer, si hoy aún no está hecho).
  let currentStreak = 0;
  let cursor = has(today) ? today : addDays(today, -1);
  while (has(cursor)) { currentStreak += 1; cursor = addDays(cursor, -1); }

  // Mejor racha y recuperación, recorriendo desde el primer día cumplido hasta hoy.
  const first = [...done].filter(k => k <= today).sort()[0];
  if (!first) return { ...EMPTY_STATS, last7: countBack(7), last30: countBack(30) };
  let best = 0, run = 0, gap = 0, recovered = 0, opportunities = 0;
  for (let d = first; d <= today; d = addDays(d, 1)) {
    if (has(d)) {
      run += 1;
      best = Math.max(best, run);
      if (gap > 0) { opportunities += 1; if (gap === 1) recovered += 1; gap = 0; }
    } else if (d < today) {
      run = 0;
      gap += 1;
    }
  }
  // Un hueco abierto de 2+ días ya es "fallar dos veces": se valora, sin recuperar.
  if (gap >= 2) opportunities += 1;

  return { last7: countBack(7), last30: countBack(30), currentStreak, bestStreak: best, recovered, recoveryOpportunities: opportunities };
}
